import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import LoadingSpinner from "../LoadingSpinner";
import SearchDropdown from "../input/SearchDropdown";
import {
  getFieldValuesByTableAndId,
  getOtherRefSearchDropdown,
  getTextByValueAndKey,
} from "../../api/keyvalue";
import {locationCustomerValidation} from "../validations/locationCreation";
import Grid from "../Grid";
import Input from "../input/Input";
import Dropdown from "../input/Dropdown";
import ButtonRenderer from "../renderer/ButtonRenderer";
import Button from "../input/Button";
import { ResizableScrollingTextRenderer } from "../renderer/StyleRenderer";
import { AUTH_TYPES, FORMS, MODULES } from "../../const";
import { isNumber } from "../../helper";
import { useSelector } from "react-redux";
import { shipmentsByCustomer } from "../../api/customerShipmentApi";
import DeleteButtonRenderer from "../renderer/DeleteButtonRenderer";

const FORM = FORMS.LOCATION.LOC_CUSTOMER.form;

const LocationCustomer = forwardRef(
  ({ isLoading, dropdown, currentValues, editMode, locationData }, ref) => {
    const userData = useSelector((state) => state.user.userDetails);
    const user = useSelector((state) => state.user);

    const [currentErrors, setCurrentErrors] = useState({});
    const [locationCustomers, setLocationCustomers] = useState([]);
    const [currentLocationCustomer, setCurrentLocationCustomer] = useState({});
    const [deletedList, setDeletedList] = useState([]);
    const [buttonClicked, setButtonClicked] = useState(false);
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [lcuCount, setLcuCount] = useState(2);
    const [operationTypeOptions, setOperationTypeOptions] = useState([]);

    const [locationCustomer, setLocationCustomer] = useState({
      lcuReference: "",
      lcuLocation: "",
      lcuCustomer: "",
      lcuCustomerName: "",
      lcuFenceName: "",
      lcuLocLon: "",
      lcuLocLat: "",
      lcuIcon: "",
      lcuColor: "",
      lcuDistributorCode: "",
      lcuLocationCode: "",
      lcuStatus: 1,
      lcuStatusName: "",
      lcuActive: 1,
      lcuActiveValue: "",
      lcuShipper: "",
      lcuShipperName: "",
      lcuDistanceToShipper: "",
      lcuShipmentType: "",
      lcuShipmentTypeValue: "",
      lcuEmailRecieverList: "",
      lcuSmsRecieverList: "",
    });

    const [searchFieldReset, setSearchFieldReset] = useState(false);

    useEffect(() => {
      (async () => {
        if (locationCustomer.lcuCustomer) {
          const data = await shipmentsByCustomer(locationCustomer.lcuCustomer);
          setOperationTypeOptions(data);
        } else {
          setOperationTypeOptions([]);
        }
      })();
    }, [locationCustomer.lcuCustomer]);

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentLocationCustomers = [];
          let id = 2;
          for (let currentValue of currentValues) {
            currentLocationCustomers.push({
              ...currentValue,
              lcuLocation: currentValue.lcuLocationId,
              lcuCustomer: currentValue.lcuCustomerId,
              lcuShipper: currentValue.lcuShipper,
              lcuEmailRecieverList: currentValue.lcuEmailRecieverList || "",
              lcuSmsRecieverList: currentValue.lcuSmsRecieverList || "",
              lcuDistanceToShipper: currentValue.lcuDistanceToShipper || "",
              lcuShipmentType: currentValue.lcuShipmentType,

              id,
            });
            ++id;
          }
          setLcuCount(id);
          setLocationCustomers(currentLocationCustomers);
        }
      })();
    }, [editMode, currentValues]);

    useEffect(() => {
      if (buttonClicked) {
        setLocationCustomer({
          lcuReference: "",
          lcuLocation: "",
          lcuCustomer: "",
          lcuCustomerName: "",
          lcuFenceName: "",
          lcuLocLon: "",
          lcuLocLat: "",
          lcuIcon: "",
          lcuColor: "",
          lcuDistributorCode: "",
          lcuLocationCode: "",
          lcuStatus: 1,
          lcuStatusName: "",
          lcuActive: 1,
          lcuActiveValue: "",
          lcuShipper: "",
          lcuShipperName: "",
          lcuDistanceToShipper: "",
          lcuShipmentType: "",
          lcuShipmentTypeValue: "",
          lcuEmailRecieverList: "",
          lcuSmsRecieverList: "",
        });
        setSearchFieldReset(true);
        setCurrentErrors({});
        setButtonClicked(false);
        setCurrentLocationCustomer({});
      }
    }, [buttonClicked]);

    useEffect(() => {
      if (Object.keys(currentLocationCustomer).length > 0) {
        setLocationCustomer({
          ...currentLocationCustomer,
          lcuActiveValue: currentLocationCustomer.lcuActiveValue,
          lcuStatusName: currentLocationCustomer.lcuStatusName,
          lcuCustomerName: currentLocationCustomer.lcuCustomerName,
          lcuShipperName: currentLocationCustomer.lcuShipperName,
          lcuShipmentTypeValue: currentLocationCustomer.lcuShipmentTypeValue,
          lcuDistanceToShipper:
            currentLocationCustomer.lcuDistanceToShipper?.toString() || "",
        });
      }
    }, [editMode, currentLocationCustomer]);

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          const updateState = {
            modified: [],
            created: [],
            deleted: deletedList,
          };

          locationCustomers.forEach((lcu) => {
            if (lcu.lcuId) {
              if (lcu.modified) {
                updateState.modified.push(lcu);
              }
            } else {
              updateState.created.push(lcu);
            }
          });

          return updateState;
        } else {
          return locationCustomers;
        }
      },
      resetForm: () => {
        setButtonClicked(true);
        setLocationCustomers([]);
        setSearchFieldReset(true);
        setCurrentErrors({});
      },
      validateForm: () => {
        const errors = {};
        if (!locationCustomers || locationCustomers.length === 0) {
          errors.mainCustomerError = "At least one Location Customer is required";
        }
        setCurrentErrors(errors);
        return errors;
      },
    }));

    const onAddClick = async () => {
      setIsAddLoading(true);

      let locationCustomerErrors = locationCustomerValidation(locationCustomer);
      if (locationData === null || locationData === "") {
        locationCustomerErrors = {
          ...locationCustomerErrors,
          lcuLocation: "Location is required",
        };
      }
      if (Object.keys(locationCustomerErrors).length === 0) {
        const isDuplicate = locationCustomers.some(
          (lcu) =>
            lcu.lcuCustomer === locationCustomer.lcuCustomer &&
            (!locationCustomer.lcuId || lcu.lcuId !== locationCustomer.lcuId) &&
            lcu.id !== locationCustomer.id
        );

        if (isDuplicate) {
          locationCustomerErrors.lcuCustomer = "Customer already added";
          setCurrentErrors(locationCustomerErrors);
          setIsAddLoading(false);
          return;
        }
        const lcuActiveValue = await getTextByValueAndKey(
          "active",
          locationCustomer.lcuActive
        );
        const lcuStatusName = await getTextByValueAndKey(
          "status",
          locationCustomer.lcuStatus
        );
        const customerShipmentOptions = await shipmentsByCustomer(
          locationCustomer.lcuCustomer
        );
        const lcuShipmentTypeValue =
          customerShipmentOptions.find(
            (opt) => opt.value === locationCustomer.lcuShipmentType
          )?.text || "";

        const lcuCustomerResponse = await getFieldValuesByTableAndId({
          tableName: "masterdata.tms_m_stakeholder",
          primaryKey: "stk_id",
          fieldNames: "stk_name",
          ids: locationCustomer.lcuCustomer,
        });

        let lcuShipperName = "";
        if (locationCustomer.lcuShipper) {
          const lcuShipperResponse = await getFieldValuesByTableAndId({
            tableName: "masterdata.tms_m_stakeholder",
            primaryKey: "stk_id",
            fieldNames: "stk_name",
            ids: locationCustomer.lcuShipper,
          });
          lcuShipperName = lcuShipperResponse[0].field1;
        }

        const lcuCustomerName = lcuCustomerResponse[0].field1;

        if (!locationCustomer.lcuId) {
          const distanceValue = locationCustomer.lcuDistanceToShipper
            ? parseFloat(locationCustomer.lcuDistanceToShipper)
            : null;
          if (!locationCustomer.id) {
            setLocationCustomers((prevState) => [
              ...prevState,
              {
                ...locationCustomer,
                lcuActiveValue,
                lcuStatusName,
                lcuCustomerName,
                lcuShipperName,
                lcuShipmentTypeValue,
                lcuDistanceToShipper: distanceValue,
                lcuEmailRecieverList: locationCustomer.lcuEmailRecieverList,
                lcuSmsRecieverList: locationCustomer.lcuSmsRecieverList,
                id: lcuCount,
              },
            ]);
            setLcuCount((prevState) => prevState + 1);
          } else {
            setLocationCustomers((prevState) =>
              prevState.filter((item) => item.id !== locationCustomer.id)
            );
            setLocationCustomers((prevState) => [
              ...prevState,
              {
                ...locationCustomer,
                lcuActiveValue,
                lcuStatusName,
                lcuShipperName,
                lcuShipmentTypeValue,
                lcuDistanceToShipper: distanceValue,
                lcuEmailRecieverList: locationCustomer.lcuEmailRecieverList,
                lcuSmsRecieverList: locationCustomer.lcuSmsRecieverList,
                lcuCustomerName,
              },
            ]);
          }
        } else {
          setLocationCustomers((prevState) =>
            prevState.filter((item) => item.lcuId !== locationCustomer.lcuId)
          );
          setLocationCustomers((prevState) => [
            ...prevState,
            {
              ...locationCustomer,
              lcuActiveValue,
              lcuStatusName,
              lcuCustomerName,
              lcuShipperName,
              lcuShipmentTypeValue,
              lcuDistanceToShipper:
                parseFloat(locationCustomer.lcuDistanceToShipper) || null,
              lcuEmailRecieverList: locationCustomer.lcuEmailRecieverList,
              lcuSmsRecieverList: locationCustomer.lcuSmsRecieverList,
              modified: true,
            },
          ]);
        }
        setButtonClicked(true);
      }

      setCurrentErrors(locationCustomerErrors);
      setIsAddLoading(false);
    };

    console.log("location customer", locationCustomer);
    console.log("location customers", locationCustomers);

    return (
      <>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <form className="ui form">
            <div className="ui segment">
              <h4 className="ui dividing header">Location Customer</h4>
              <div className="field">
                <Input
                  required={true}
                  label="Location"
                  error={currentErrors.lcuLocation}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_location"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuLocation: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="lcuLocation"
                  value={locationData}
                  disabled
                />
              </div>
              <div className="two fields">
                <SearchDropdown
                  key={1}
                  multiGrid={true}
                  multiGridCurrentValue={locationCustomer.lcuCustomerName}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_customer"
                  editMode={editMode}
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuCustomer: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuCustomer: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={
                    userData.type === 1
                      ? {
                          type: "stakeholder_type",
                          value: "Customer",
                        }
                      : {
                          type: "user_stakeholder",
                          value: "Requesting Customer",
                          param2: user.username,
                        }
                  }
                  fieldInfo={{
                    tableName: "masterdata.tms_m_stakeholder",
                    primaryKey: "stk_id",
                    fieldName: "stk_name",
                    id: locationCustomer.lcuCustomer,
                  }}
                  label="Customer"
                  required={true}
                  error={currentErrors.lcuCustomer}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuCustomer: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
                <Input
                  label="Color"
                  error={currentErrors.lcuColor}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_color"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuColor: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="lcuColor"
                  value={locationCustomer.lcuColor}
                  onChange={(e) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuColor: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="two fields">
                <SearchDropdown
                  key={1}
                  label="Shipper"
                  multiGrid={true}
                  multiGridCurrentValue={locationCustomer.lcuShipperName}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_shipper"
                  reset={searchFieldReset}
                  setReset={setSearchFieldReset}
                  clearable={true}
                  onClear={() => {
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuShipper: null,
                    }));
                  }}
                  onResultSelect={(data) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuShipper: data,
                    }))
                  }
                  api={getOtherRefSearchDropdown}
                  apiDependency={{
                    type: "stakeholder_type",
                    value: "Shipper",
                  }}
                  fieldInfo={{
                    tableName: "masterdata.tms_m_stakeholder",
                    primaryKey: "stk_id",
                    fieldName: "stk_name",
                    id: locationCustomer.lcuShipper,
                  }}
                  error={currentErrors.lcuShipper}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuShipper: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                />
                <Input
                  label="Distance to Shipper"
                  error={currentErrors.lcuDistanceToShipper}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_distance_to_shipper"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuDistanceToShipper: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="lcuDistanceToShipper"
                  type="number"
                  value={locationCustomer.lcuDistanceToShipper}
                  onChange={(e) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuDistanceToShipper: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="two fields">
                <Dropdown
                  label="Shipment Type"
                  error={currentErrors.lcuShipmentType}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_shipment_type"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuShipmentType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={operationTypeOptions}
                  style={{ height: "20px" }}
                  placeholder="Shipment Type"
                  value={locationCustomer.lcuShipmentType}
                  onChange={(e, data) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuShipmentType: data.value,
                    }))
                  }
                />
              </div>

              <Input
                label="Email Receiver List (Enter Values in Comma Separatly)"
                error={currentErrors.lcuEmailRecieverList}
                module={MODULES.Master}
                form={FORM}
                field="lcu_email_reciever_list"
                editMode={editMode}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { lcuEmailRecieverList: _, ...rest } = prevState;
                    return rest;
                  })
                }
                name="lcuEmailRecieverList"
                value={locationCustomer.lcuEmailRecieverList}
                onChange={(e) =>
                  setLocationCustomer((prevState) => ({
                    ...prevState,
                    lcuEmailRecieverList: e.target.value,
                  }))
                }
              />
              <Input
                label="SMS Receiver List (Enter Values in Comma Separatly)"
                error={currentErrors.lcuSmsRecieverList}
                module={MODULES.Master}
                form={FORM}
                field="lcu_sms_reciever_list"
                editMode={editMode}
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { lcuSmsRecieverList: _, ...rest } = prevState;
                    return rest;
                  })
                }
                name="lcuSmsRecieverList"
                value={locationCustomer.lcuSmsRecieverList}
                onChange={(e) =>
                  setLocationCustomer((prevState) => ({
                    ...prevState,
                    lcuSmsRecieverList: e.target.value,
                  }))
                }
              />
              <div className="two fields">
                <Input
                  label="Distributor Code"
                  error={currentErrors.lcuDistributorCode}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_distributor_code"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuDistributorCode: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="lcuDistributorCode"
                  value={locationCustomer.lcuDistributorCode}
                  onChange={(e) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuDistributorCode: e.target.value,
                    }))
                  }
                />
                <Input
                  label="Location Code"
                  error={currentErrors.lcuLocationCode}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_location_code"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuLocationCode: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="lcuLocationCode"
                  value={locationCustomer.lcuLocationCode}
                  onChange={(e) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuLocationCode: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="two fields">
                <Input
                  label="Icon"
                  error={currentErrors.lcuIcon}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_icon"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuIcon: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  name="lcuIcon"
                  value={locationCustomer.lcuIcon}
                  onChange={(e) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuIcon: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="two fields">
                <Dropdown
                  required={true}
                  label="Status"
                  error={currentErrors.lcuStatus}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_status"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuStatus: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.statusOptions}
                  style={{ height: "20px" }}
                  placeholder="Status"
                  value={locationCustomer.lcuStatus}
                  onChange={(e, data) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuStatus: data.value,
                    }))
                  }
                />

                {/* <Dropdown
                  required={true}
                  label="Active"
                  error={currentErrors.lcuActive}
                  module={MODULES.Master}
                  form={FORM}
                  field="lcu_active"
                  editMode={editMode}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { lcuActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  options={dropdown.activeOptions}
                  style={{ height: "20px" }}
                  placeholder="Active"
                  value={locationCustomer.lcuActive}
                  onChange={(e, data) =>
                    setLocationCustomer((prevState) => ({
                      ...prevState,
                      lcuActive: data.value,
                    }))
                  }
                /> */}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                justifyContent: "flex-end",
                gap: "5px",
              }}
            >
              <Button
                template="reset"
                onClick={(e) => {
                  setButtonClicked(true);
                }}
              />
              <Button
                template="addOrSave"
                authenticated={true}
                editMode={editMode}
                module={MODULES.Master}
                form={`${FORM}`}
                create={Object.keys(currentLocationCustomer).length === 0}
                onClick={onAddClick}
                isLoading={isAddLoading}
              />
            </div>
            <Grid
              heading="Location Customers"
              config={[
                {
                  headerName: "Customer",
                  field: "lcuCustomerName",
                  cellRenderer: ResizableScrollingTextRenderer,
                },
                {
                  headerName: "Shipper",
                  field: "lcuShipperName",
                  cellRenderer: ResizableScrollingTextRenderer,
                },

                { headerName: "Distance", field: "lcuDistanceToShipper" },
                {
                  headerName: "Shipment Type",
                  cellRenderer: ResizableScrollingTextRenderer,
                  field: "lcuShipmentTypeValue",
                },
                {
                  headerName: "Email Receiver List",
                  cellRenderer: ResizableScrollingTextRenderer,
                  field: "lcuEmailRecieverList",
                },
                {
                  headerName: "SMS Receiver List",
                  cellRenderer: ResizableScrollingTextRenderer,
                  field: "lcuSmsRecieverList",
                },
                { headerName: "Color", field: "lcuColor" },
                { headerName: "Icon", field: "lcuIcon" },
                { headerName: "Status", field: "lcuStatusName" },
                { headerName: "Active", field: "lcuActiveValue" },
                {
                  field: "Edit",
                  filter: true,
                  cellRenderer: ButtonRenderer,
                  cellRendererParams: (params) => ({
                    clickHandler: () => {
                      setCurrentLocationCustomer(params.data);
                    },
                    label: "Edit",
                    authenticated: true,
                    form: `${FORM}`,
                    module: MODULES.Master,
                    editMode: editMode,
                    consider: AUTH_TYPES.CREATE,
                  }),
                },
                {
                  field: "Delete",
                  filter: true,
                  cellRenderer: DeleteButtonRenderer,
                  cellRendererParams: (params) => ({
                    consider: AUTH_TYPES.DELETE,
                    clickHandler: () => {
                      if (params.data.lcuId && params.data.lcuId !== null) {
                        setDeletedList((prevState) => [
                          ...prevState,
                          params.data.lcuId,
                        ]);
                      }
                      setLocationCustomers((prevLcu) =>
                        prevLcu.filter((lcu) => lcu.id !== params.data.id)
                      );
                    },
                    message:
                          "Are you sure you want to delete this Location Customer?",
                    label: "Delete",
                    className: "ui red button",
                    authenticated: true,
                    form: `${FORM}`,
                    module: MODULES.Master,
                    editMode:
                      params.data.lcuId !== null && isNumber(params.data.lcuId),
                  }),
                },
              ]}
              content={locationCustomers}
            />
          </form>
        )}
      </>
    );
  }
);

export default LocationCustomer;
